import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Minus, Plus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { CheckoutPanel } from "./CheckoutPanel";
import { CreateAccountCta } from "@/components/account/CreateAccountCta";
import { formatPrice, FREE_SHIPPING, SHIPPING_FEE } from "@/lib/site";
import { startScroll, stopScroll } from "@/lib/lenis";

const CartLine = ({ item, setQty, onNavigate }) => (
  <li data-testid={`cart-item-${item.key}`} className="flex gap-4 border-b border-line py-4">
    <img src={item.image} alt={item.name} className="h-24 w-20 object-cover grayscale" />
    <div className="flex flex-1 flex-col justify-between">
      <div className="flex justify-between gap-3">
        <div>
          <Link to={`/prodotto/${item.slug || item.product_id}`} onClick={onNavigate} data-testid={`cart-item-link-${item.key}`} className="font-display text-sm font-bold uppercase transition-colors hover:text-acid">{item.name}</Link>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">{item.variant_id ? item.size : `Taglia ${item.size}`}</p>
        </div>
        <p className="font-mono text-sm">{formatPrice(item.price * item.qty)}</p>
      </div>
      <div className="flex items-center gap-1">
        <button data-testid={`cart-decrease-${item.key}`} onClick={() => setQty(item.key, item.qty - 1)} className="flex h-8 w-8 items-center justify-center border border-line hover:border-cream" aria-label="Diminuisci">
          <Minus className="h-3 w-3" />
        </button>
        <span data-testid={`cart-qty-${item.key}`} className="w-8 text-center font-mono text-sm">{item.qty}</span>
        <button data-testid={`cart-increase-${item.key}`} onClick={() => setQty(item.key, item.qty + 1)} className="flex h-8 w-8 items-center justify-center border border-line hover:border-cream" aria-label="Aumenta">
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  </li>
);

const Summary = ({ subtotal, shipping }) => (
  <div className="space-y-2 font-mono text-xs uppercase tracking-[0.15em]">
    <div className="flex justify-between text-smoke"><span>Subtotale</span><span data-testid="cart-subtotal">{formatPrice(subtotal)}</span></div>
    <div className="flex justify-between text-smoke"><span>Spedizione</span><span data-testid="cart-shipping">{shipping ? formatPrice(shipping) : "Gratis"}</span></div>
    <div className="flex justify-between pt-2 text-base text-bone"><span>Totale</span><span data-testid="cart-total">{formatPrice(subtotal + shipping)}</span></div>
  </div>
);

const Done = ({ order, onClose }) => (
  <div data-testid="checkout-success" className="flex flex-1 flex-col items-start justify-center gap-5 overflow-y-auto px-6 py-8" data-lenis-prevent>
    <CheckCircle2 className="h-12 w-12 text-cream" />
    <h3 className="font-display text-3xl font-black uppercase leading-none">Pagamento confermato.</h3>
    <p className="text-bone/70">Grazie per aver scelto sfushop! Il tuo ordine <span className="font-mono text-cream">#{order.order_number}</span> è confermato: ti ho inviato un'email con il riepilogo.</p>
    <p className="border border-line p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">Pagato con PayPal · il tuo pezzo parte entro 48 ore.</p>
    <CreateAccountCta testid="drawer-account-cta" />
    <button data-testid="checkout-done-button" onClick={onClose} className="h-12 w-full bg-cream font-mono text-xs uppercase tracking-[0.2em] text-ink">Torna al sito</button>
  </div>
);

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, subtotal, clear } = useCart();
  const [step, setStep] = useState("cart");
  const [order, setOrder] = useState(null);
  const shipping = subtotal === 0 || subtotal >= FREE_SHIPPING ? 0 : SHIPPING_FEE;

  useEffect(() => {
    if (open) stopScroll();
    else {
      startScroll();
      setStep((s) => (s === "done" ? "cart" : s));
    }
  }, [open]);

  const onPaid = (data) => {
    setOrder(data);
    clear();
    setStep("done");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="cart-drawer" data-lenis-prevent className="flex w-full flex-col gap-0 border-l border-line bg-ink-2 p-0 text-bone sm:max-w-md">
        <div className="border-b border-line px-6 py-5">
          <SheetTitle className="font-display text-xl font-black uppercase tracking-tight text-bone">Il tuo carrello</SheetTitle>
          <SheetDescription className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">sfushop · Drop 01</SheetDescription>
        </div>

        {step === "done" && order ? (
          <Done order={order} onClose={() => setOpen(false)} />
        ) : items.length === 0 ? (
          <div data-testid="cart-empty" className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="h-10 w-10 text-smoke" />
            <p className="font-display text-lg font-bold uppercase">Ancora vuoto.</p>
            <p className="text-sm text-bone/60">Il drop è limitato: scegli il tuo pezzo prima che sparisca.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6" data-lenis-prevent>
              {shipping > 0 && (
                <p className="mt-4 bg-ink-3 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                  Ti mancano <span className="text-cream">{formatPrice(FREE_SHIPPING - subtotal)}</span> per la spedizione gratuita
                </p>
              )}
              <ul>{items.map((i) => <CartLine key={i.key} item={i} setQty={setQty} onNavigate={() => setOpen(false)} />)}</ul>
            </div>
            <div className="space-y-5 border-t border-line px-6 py-6">
              <Summary subtotal={subtotal} shipping={shipping} />
              {step === "cart" ? (
                <button data-testid="cart-checkout-button" onClick={() => setStep("checkout")} className="h-12 w-full bg-cream font-mono text-xs uppercase tracking-[0.2em] text-ink transition-transform active:scale-[0.98]">
                  Procedi all'ordine
                </button>
              ) : (
                <CheckoutPanel onPaid={onPaid} />
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
